import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Youtube } from "lucide-react";
import { KNOW_IT_OWLS_LOGO, WALL_OF_ENT_LOGO } from "@/data/products";

const SOCIALS = [
  { label: "TikTok", href: "https://www.tiktok.com/@wallofentertainment", testId: "social-tiktok" },
  { label: "Instagram", href: "https://www.instagram.com/wallofentertainment/", testId: "social-instagram", Icon: Instagram },
  { label: "YouTube", href: "https://www.youtube.com/@wallofentertainment", testId: "social-youtube", Icon: Youtube },
];

const COLUMNS = [
  {
    title: "May We Help You",
    links: [
      { label: "Contact Us", href: "mailto:hello@wallofentertainment.com", testId: "footer-link-contact" },
      { label: "Track My Order", to: "/track", testId: "footer-link-track" },
      { label: "FAQ", to: "/faq", testId: "footer-link-faq" },
      { label: "Sitemap", href: "/sitemap.xml", testId: "footer-link-sitemap" },
    ],
  },
  {
    title: "About",
    links: [
      { label: "Wall of Entertainment", href: "https://wallofentertainment.com/", testId: "footer-link-woe" },
      { label: "Charles Brockman III", href: "https://www.charlesbrockmaniii.com/", testId: "footer-link-cb3" },
      { label: "Tubi TV", href: "https://tubitv.com/", testId: "footer-link-tubi" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Shipping", to: "/shipping", testId: "footer-link-shipping" },
      { label: "Returns", to: "/returns", testId: "footer-link-returns" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[var(--st-maroon-deep)] text-white border-t-2 border-[#111] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-14 md:pt-14 pb-4 md:pb-2 flex flex-col gap-10 md:grid md:grid-cols-12 md:gap-6">
        <div className="md:col-span-3 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2 md:gap-3">
            <img src={KNOW_IT_OWLS_LOGO} alt="The Know It Owls" className="h-16" />
            <div className="leading-none text-center md:text-left">
              <p className="font-pixel text-xl text-[var(--st-gold)]">THE</p>
              <p className="font-display text-base text-white -mt-1">KNOW IT OWLS</p>
            </div>
          </div>
          <p className="mt-6 md:mt-5 font-body text-sm text-white/70 leading-relaxed max-w-[18rem] md:max-w-sm mx-auto md:mx-0 px-2 md:px-0">
            Substitute Teacher is a Wall of Entertainment production.
            <br className="hidden md:inline" />{" "}
            Streaming on Tubi TV.
          </p>
          <a
            href="https://wallofentertainment.com/"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-woe-logo"
            aria-label="Wall of Entertainment"
            className="mt-5 md:mt-4 inline-flex items-center gap-3 bg-white/95 border-2 border-[#111] px-4 py-2.5 shadow-brutal-sm hover:bg-white transition-colors"
          >
            <span className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-maroon)]">
              A
            </span>
            <img
              src={WALL_OF_ENT_LOGO}
              alt="Wall of Entertainment"
              className="h-6 md:h-7 w-auto"
              loading="lazy"
              decoding="async"
            />
            <span className="font-heading uppercase tracking-[0.22em] text-[10px] text-[var(--st-maroon)]">
              Production
            </span>
          </a>
          <div className="mt-6 md:mt-5 flex flex-wrap gap-2 justify-center md:justify-start">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                data-testid={s.testId}
                className="inline-flex items-center gap-1.5 border-2 border-[var(--st-gold)] px-3 py-1.5 text-xs font-heading uppercase tracking-[0.18em] hover:bg-[var(--st-gold)] hover:text-[var(--st-fg)] transition-colors"
              >
                {s.Icon ? <s.Icon size={12} /> : null}
                {s.label}
              </a>
            ))}
          </div>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.title} className="col-span-12 md:col-span-2 text-center md:text-left">
            <p className="font-display uppercase tracking-tight text-xl text-[var(--st-gold)]">
              {col.title}
            </p>
            <ul className="mt-5 md:mt-4 flex flex-col gap-3 md:gap-2 items-center md:items-start">
              {col.links.map((l) => {
                const cls =
                  "font-body text-sm text-white/80 hover:text-[var(--st-orange)] transition-colors";
                if (l.to) {
                  return (
                    <li key={l.label}>
                      <Link to={l.to} data-testid={l.testId} className={cls}>
                        {l.label}
                      </Link>
                    </li>
                  );
                }
                const isExternal = l.href.startsWith("http");
                return (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noreferrer" : undefined}
                      data-testid={l.testId}
                      className={cls}
                    >
                      {l.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <div className="col-span-12 md:col-span-3 text-center md:text-left">
          <p className="font-display uppercase tracking-tight text-2xl text-[var(--st-gold)]">
            Have a question?
          </p>
          <p className="mt-2 font-body text-sm text-white/70 max-w-sm mx-auto md:mx-0">
            Hit the line. We answer faster than the bell.
          </p>
          <a
            href="mailto:hello@wallofentertainment.com"
            data-testid="contact-cta"
            className="mt-5 md:mt-4 inline-flex items-center gap-2 bg-[var(--st-orange)] text-[var(--st-fg)] px-4 py-2 border-2 border-[#111] font-heading uppercase tracking-[0.18em] text-sm hover:bg-[var(--st-gold)] transition-colors shadow-brutal-sm"
          >
            Contact Us <ArrowUpRight size={14} />
          </a>
        </div>
      </div>

      {/* SEO/AI-discovery block — keeps key entities + Revurge attribution
          in crawlable body text so search engines + LLMs can index the
          show, its star, and the team that built this storefront. */}
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 mt-8 md:mt-6 mb-4">
        <div className="border-t-2 border-white/15 pt-8 md:pt-6 grid grid-cols-12 gap-6 text-white/75 font-body text-[13px] leading-relaxed">
          <div className="col-span-12 md:col-span-9 text-center md:text-left">
            <p>
              <strong className="text-white">Substitute Teacher</strong> is the new
              streaming series from{" "}
              <a
                href="https://wallofentertainment.com/"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-[var(--st-gold)] hover:text-[var(--st-orange)]"
              >
                Wall of Entertainment
              </a>{" "}
              starring{" "}
              <a
                href="https://www.charlesbrockmaniii.com/"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-[var(--st-gold)] hover:text-[var(--st-orange)]"
              >
                Charles Brockman III
              </a>{" "}
              (also known as Charles Brockman, CB3, or Charles Brockman 3) —
              the actor, comedian and creator. New
              episodes drop on{" "}
              <a
                href="https://tubitv.com/"
                target="_blank"
                rel="noreferrer"
                className="underline decoration-[var(--st-gold)] hover:text-[var(--st-orange)]"
              >
                Tubi TV
              </a>
              . Shop the official hoodies, sweatshirts, tees, and cropped tees
              of the show — class is in session.
            </p>
          </div>
          <div className="col-span-12 md:col-span-3 text-center md:text-right mt-4 md:mt-0">
            <p className="font-heading uppercase tracking-[0.22em] text-[10px] text-white/50 mb-1">
              An official merchandise drop
            </p>
            <a
              href="https://revurge.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-revurge-attribution"
              className="inline-flex items-center gap-2 group"
            >
              <span className="font-display uppercase tracking-tight text-base text-[var(--st-gold)] group-hover:text-[var(--st-orange)] transition-colors">
                Designed, Printed &amp; Fulfilled by Revurge
              </span>
              <ArrowUpRight size={14} className="text-[var(--st-gold)] group-hover:text-[var(--st-orange)] transition-colors" />
            </a>
            <p className="font-body text-xs text-white/55 mt-1">
              Print-on-demand, technology &amp; logistics partner
            </p>
          </div>
        </div>
      </div>

      {/* Massive wordmark */}
      <div className="relative pt-6 pb-2 overflow-hidden">
        <p className="font-display uppercase leading-[0.82] tracking-tight text-center text-[20vw] text-white/10 select-none">
          SUBSTITUTE
        </p>
      </div>
      <div className="border-t-2 border-white/20 text-center py-4 px-4 text-xs font-heading uppercase tracking-[0.22em] text-white/60">
        © {new Date().getFullYear()} Wall of Entertainment · The Know It Owls · All rights reserved.
        {" "}·{" "}
        <a
          href="https://revurge.com"
          target="_blank"
          rel="noreferrer"
          className="text-[var(--st-gold)] hover:text-[var(--st-orange)] transition-colors"
        >
          A Revurge Build
        </a>
      </div>
    </footer>
  );
}
